function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useContext, useMemo, useRef, forwardRef } from 'react';
import cn from 'clsx';
import { string, bool, shape, array } from 'prop-types';
import { LoggedInContext } from '@immediate_media/component-config';
import BrandedSection from '../BrandedSection/index.jsx';
import MainNav from '../MainNav/index.jsx';
import Image from '../../atoms/Image/index.jsx';
import PianoInlineTemplate from '../../atoms/PianoInlineTemplate/index.jsx';
import EyebrowNav from '../../molecules/EyebrowNav/index.jsx';
import SidebarNav from '../../molecules/SidebarNav/index.jsx';
import PopupContext from '../../context/PopupContext.js';
import AccountNav from '../../molecules/AccountNav/index.jsx';
import AuthDropDown from '../../molecules/AuthDropDown/index.jsx';
import { useIsDesktop } from '../../hooks/index.js';
import { useStickyToggle, useActivePopup, useIsTakeover } from './hooks.js';
const popupKeys = {
  mainNav: 'main-navigation',
  search: 'search',
  account: 'account'
};

/**
 * A wrapper that lets us optionally use pure CSS stickiness.
 *
 * Safari has a bug where nested sticky elements are not repainted correctly
 * (see https://bugs.webkit.org/show_bug.cgi?id=106062). Hacks to force gpu
 * acceleration or repaints don't seem to work. The solution is to not have a
 * containing header element. However, when distributing this component
 * individually (e.g. via our static component setup) the component must have
 * a wrapper so that we have something to target for client-side hydration. So,
 * here we give the option of wrapping the header elements in a container, or
 * not. This essentially means we can have nice smooth CSS stickiness for our
 * new services but fallback to a prone to jankiness JS fixed positioning
 * toggle for our WordPress application. Once WP is no longer used for our
 * frontend this second option can go.
 */
const Wrapper = /*#__PURE__*/forwardRef(({
  /* eslint-disable react/prop-types */
  wrap,
  children,
  ...restProps
  /* eslint-enable react/prop-types */
}, ref) => {
  if (!wrap) {
    return children;
  }
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref
  }, restProps), children);
});

/**
 * Get the Whisk menu item.
 */
const getWhiskMenuItem = ({
  trackingId,
  whiteLabel,
  text
}) => ({
  label: text,
  title: text,
  relativeUrl: '#',
  onClick: evt => {
    evt.preventDefault();
    const whisk = window.whisk || {};
    whisk.queue = whisk.queue || [];
    whisk.queue.push(() => {
      whisk.shoppingList.viewList({
        trackingId,
        whiteLabel
      });
    });
  }
});

/**
 * SiteHeader component.
 */
const SiteHeader = ({
  brandedSectionLogo,
  brandedSectionLogoAlignment,
  strapline,
  search,
  eyebrowEnabled,
  eyebrowMenuItems,
  eyebrowLogo,
  mobileLogo,
  logoUrl,
  mainNavMenuItems,
  profileDetails,
  accountNavOptions,
  className,
  whiskSettings,
  fixedPositioning,
  brandedSectionBackgroundImage,
  mobilePromoLogoImage,
  desktopPromoLogoImage,
  desktopPromoLogoAlignment,
  promoLogoUrl
}) => {
  const eyebrowRef = useRef(null);
  const navRef = useRef(null);
  const headerRef = useRef(null);
  const sponsoredRef = useRef(null);
  const eyebrowMenu = [...eyebrowMenuItems];
  if (whiskSettings === null || whiskSettings === void 0 ? void 0 : whiskSettings.active) {
    eyebrowMenu.push(getWhiskMenuItem(whiskSettings));
  }
  const isDesktop = useIsDesktop();
  const isLoggedIn = useContext(LoggedInContext);
  const [activePopup, setActivePopup] = useActivePopup({
    isDesktop
  });
  const isSticky = useStickyToggle({
    isDesktop,
    fixedPositioning,
    headerRef,
    eyebrowRef,
    navRef,
    overrides: eyebrowEnabled ? undefined : {
      eyebrowHeight: 35
    }
  });
  const [isTakeover, backgroundColor] = useIsTakeover({
    sponsoredRef
  });
  const contextValue = useMemo(() => ({
    activePopup,
    setActivePopup,
    popupKeys: {
      ...popupKeys,
      search: search && popupKeys.search
    }
  }), [activePopup, setActivePopup, popupKeys, search]);

  // This hides the eyebrow if not enabled on desktop only, but reappears when sticky.
  const hideEyebrowNav = !eyebrowEnabled && isDesktop && !isSticky;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Wrapper, {
    wrap: fixedPositioning,
    className: cn('site-header', className, isSticky && 'site-header--sticky'),
    ref: headerRef,
    "data-feature": "Header"
  }, /*#__PURE__*/React.createElement(PopupContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement("div", {
    ref: eyebrowRef,
    className: 'site-header__eyebrow-nav hidden-print' + (hideEyebrowNav ? " site-header__eyebrow-nav--hidden" : ""),
    "data-feature": "Header"
  }, !hideEyebrowNav && /*#__PURE__*/React.createElement(EyebrowNav, {
    items: eyebrowMenu,
    logo: !isDesktop && mobileLogo ? mobileLogo : eyebrowLogo,
    logoUrl: logoUrl,
    swapContent: isSticky,
    accountNavOptions: eyebrowEnabled ? accountNavOptions : null,
    backgroundColour: backgroundColor

    // TODO: Enable avatar image when we have a way of pulling the data
    // through when using this component as a static header on WP
    // avatarImage={profileDetails.avatar}
  })), eyebrowEnabled && accountNavOptions && (isLoggedIn ? /*#__PURE__*/React.createElement("div", {
    className: "site-header__nav-section site-header__nav-section--right",
    "data-feature": "Header"
  }, /*#__PURE__*/React.createElement(SidebarNav, {
    accountNavOptions: accountNavOptions,
    profileDetails: profileDetails,
    left: false
  })) : /*#__PURE__*/React.createElement(AuthDropDown, {
    signInUrl: accountNavOptions.signInUrl,
    registerUrl: accountNavOptions.registerUrl
  })), /*#__PURE__*/React.createElement("div", {
    className: "site-header__brand",
    "data-feature": "Header"
  }, /*#__PURE__*/React.createElement(BrandedSection, {
    logo: brandedSectionLogo,
    logoAlignment: isDesktop ? brandedSectionLogoAlignment : null,
    strapline: strapline,
    search: search,
    logoUrl: logoUrl,
    backgroundImage: brandedSectionBackgroundImage,
    desktopPromoLogoImage: isDesktop ? desktopPromoLogoImage : null,
    desktopPromoLogoAlignment: desktopPromoLogoAlignment,
    promoLogoUrl: promoLogoUrl
  }), !isDesktop && mobilePromoLogoImage && promoLogoUrl && /*#__PURE__*/React.createElement("div", {
    className: "site-header__mobile-promo-logo"
  }, /*#__PURE__*/React.createElement(Image, _extends({}, mobilePromoLogoImage, {
    link: {
      href: promoLogoUrl
    },
    maxHeight: 100,
    fluid: true,
    sizes: ['100vw'],
    srcSetBreakpoints: [360, 480, 600, 740, 991],
    loading: "eager"
  }))), /*#__PURE__*/React.createElement("div", {
    className: cn('site-header__sponsored', isTakeover && "site-header__sponsored--takeover takeover" + (isSticky ? " site-header__sponsored--static" : "")),
    ref: sponsoredRef
  })), /*#__PURE__*/React.createElement("div", {
    ref: navRef,
    className: "site-header__nav-section hidden-print",
    "data-feature": "Header"
  }, /*#__PURE__*/React.createElement(MainNav, {
    menuItems: mainNavMenuItems,
    mobileMenuItems: eyebrowMenu
  })))), /*#__PURE__*/React.createElement(PianoInlineTemplate, {
    isFixed: isSticky,
    position: "high"
  }));
};
SiteHeader.defaultProps = {
  className: null,
  brandedSectionLogo: null,
  brandedSectionLogoAlignment: null,
  strapline: null,
  eyebrowEnabled: true,
  eyebrowMenuItems: [],
  eyebrowLogo: null,
  mobileLogo: null,
  logoUrl: null,
  search: null,
  mainNavMenuItems: [],
  profileDetails: {},
  accountNavOptions: null,
  whiskSettings: {},
  fixedPositioning: false,
  brandedSectionBackgroundImage: null,
  mobilePromoLogoImage: null,
  desktopPromoLogoImage: null,
  desktopPromoLogoAlignment: null,
  promoLogoUrl: null
};
SiteHeader.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** The brand logo. */
  brandedSectionLogo: shape(Image.propTypes),
  /** The brand logo alignment. */
  brandedSectionLogoAlignment: string,
  /** Strapline under the brand logo.  */
  strapline: string,
  /** Whether to show the eyebrow navigation. */
  eyebrowEnabled: bool,
  /** The eyebrow menu items. */
  eyebrowMenuItems: array,
  /** The logo to use in the eyebrow. */
  eyebrowLogo: shape(Image.propTypes),
  /** The logo to use in the eyebrow. */
  mobileLogo: shape(Image.propTypes),
  /** The URL to link to for the brand logos. */
  logoUrl: string,
  /** The search settings. */
  search: BrandedSection.propTypes.search,
  /** The main navigation menu items */
  mainNavMenuItems: MainNav.propTypes.menuItems,
  /** The sidebar drawer profile details */
  profileDetails: SidebarNav.propTypes.profileDetails,
  /** Account nav options. */
  accountNavOptions: shape(AccountNav.propTypes),
  /** Whisklink activation */
  whiskSettings: shape({
    active: bool,
    text: string,
    trackingId: string,
    whiteLabel: string
  }),
  /** Wrap the header in a containing div and use JavaScript, rather than CSS,
   * to enable the sticky behaviour. This should be considered a graceful
   * degradation for situations where we are forced to use it. */
  fixedPositioning: bool,
  /** The URL for the branded section background image */
  brandedSectionBackgroundImage: BrandedSection.propTypes.backgroundImage,
  /** The mobile promo logo image */
  mobilePromoLogoImage: shape(Image.propTypes),
  /** The desktop promo logo image */
  desktopPromoLogoImage: shape(Image.propTypes),
  /** The alignment of the desktop promo logo */
  desktopPromoLogoAlignment: string,
  /** The URL for the promo logo */
  promoLogoUrl: string
} : {};
export default SiteHeader;